declare global {
  type DataLayer = DataLayerItem[];

  type DataLayerItem = Record<
    string | number,
    string | number | Record<string, unknown> | ConsentMode | null
  >;

  interface Window {
    dataLayer: DataLayer;
  }
}

export type ConsentModeKeys =
  | "functionality_storage"
  | "security_storage"
  | "personalization"
  | "analytics_storage"
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization";
export type ConsentModeValues = "granted" | "denied" | undefined;
export type ConsentMode = Record<ConsentModeKeys, ConsentModeValues>;

export const defaultConsent: ConsentMode = {
  functionality_storage: "denied",
  security_storage: "denied",
  personalization: "denied",
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

export const CONSENT_QUERY_PARAM_KEY = "consent";

/**
 * Retrieves the consent information from the URL search parameters.
 *
 * @returns The consent information as a parsed JSON object, or undefined if consent is not found.
 */
export function getContextConsent(): ConsentMode | undefined {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const consentBase64 = urlSearchParams.get(CONSENT_QUERY_PARAM_KEY);

  if (!consentBase64) {
    return;
  }

  const decoded = atob(consentBase64);
  return JSON.parse(decoded);
}

export function getStoreConsent(tenant: string): ConsentMode | undefined {
  const lc = sessionStorage.getItem(`cookie-consent-${tenant}`);
  return lc ? JSON.parse(lc) : undefined;
}

export function setStoreConsent(tenant: string, consent: ConsentMode) {
  sessionStorage.setItem(`cookie-consent-${tenant}`, JSON.stringify(consent));
}

export function addGTMScript(gtmCode: string) {
  (function (
    w: Window & typeof globalThis,
    d: Document,
    s: "script",
    l: "dataLayer",
    i: string,
  ) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0],
      j = d.createElement(s) as HTMLScriptElement,
      dl = l != "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode?.insertBefore(j, f);
  })(window, document, "script", "dataLayer", gtmCode);
}

export function getEncodedConsent(consent: ConsentMode): string {
  return btoa(JSON.stringify(consent));
}

export function getLastConsent(dl: DataLayer): ConsentMode | undefined {
  const lc = dl.reduce(
    (acc, cur) => {
      const isConsentItem = !!cur[0] && cur[0] === "consent";
      if (!isConsentItem) return acc;

      return !!cur[2] && typeof cur[2] === "object"
        ? (cur[2] as ConsentMode)
        : acc;
    },
    undefined as ConsentMode | undefined,
  );

  return lc;
}

export function pushEventToDatalayer(
  event: string,
  dataLayerProps?: Record<string, string | number | null>,
) {
  const searchParams = new URLSearchParams(window.location.search);
  const activityTypeId = searchParams.get("activityTypeId");
  const studyProgramId = searchParams.get("studyProgramId");
  const culture = searchParams.get("culture");

  const pageDataLayerProps = {
    title: document.title,
    page:
      document.location.pathname +
      `${activityTypeId ? `/activityType/${activityTypeId}` : ""}${studyProgramId ? `/studyProgram/${studyProgramId}` : ""}`,
    pageActivityTypeId: activityTypeId,
    pageStudyProgramId: studyProgramId,
    pageCulture: culture,
  };

  window.dataLayer.push({
    event,
    ...pageDataLayerProps,
    ...dataLayerProps,
  });
}
